import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { LogOut, Search, UserPlus, Settings, Calculator, Syringe, Droplet, Bandage, Stethoscope, Activity, Wind, Trash2, Edit2, Save, X, ClipboardList, Users, PlusCircle, ListOrdered, Pill, Heart, Scissors, Thermometer, Calendar, ChevronLeft, ChevronRight, BarChart3, Wallet, Receipt, IndianRupee, Package, UserMinus, Check, Printer } from 'lucide-react';
import LiveClock from '@/components/LiveClock';
import { printDayEndReport } from '@/components/nursing/dayEndPrint';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service type icons
const SERVICE_ICONS = {
  im: Syringe,
  iv: Droplet,
  saline_100: Droplet,
  saline_500: Droplet,
  dressing: Bandage,
  suturing: Scissors,
  ear_lobe: Heart,
  procedure: Stethoscope,
  suppository: Pill,
  vaccine: Syringe,
  ecg: Activity,
  nebulization: Wind,
};

const NursingDashboard = () => {
  const { user, token, logout } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('add'); // 'add' or 'entries'
  
  // Service costs state
  const [services, setServices] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editCost, setEditCost] = useState('');
  
  // Patient type selection (separate checkboxes)
  const [patientType, setPatientType] = useState('token'); // 'token', 'walkin', 'repeat'
  
  // Patient entry state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [manualName, setManualName] = useState('');
  const [manualAge, setManualAge] = useState('');
  const [manualSex, setManualSex] = useState('');
  const [selectedServices, setSelectedServices] = useState({});
  const [serviceOverrides, setServiceOverrides] = useState({});
  const [entryNotes, setEntryNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Today's entries state
  const [todayEntries, setTodayEntries] = useState([]);
  
  // Summary state
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  
  // Today's Revenue state (nurse can see today only)
  const [showTodayRevenue, setShowTodayRevenue] = useState(false);
  const [todayRevenue, setTodayRevenue] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  // Delete day state
  const [showDeleteDay, setShowDeleteDay] = useState(false);
  const [deleteDaySummary, setDeleteDaySummary] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmDate, setDeleteConfirmDate] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  
  // History/Reports state
  const [historyTab, setHistoryTab] = useState('today'); // 'today', 'day', 'week', 'month'
  const [historyDate, setHistoryDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [historyWeekStart, setHistoryWeekStart] = useState('');
  const [historyMonth, setHistoryMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Leave & Inventory state
  const [showLeave, setShowLeave] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveName, setLeaveName] = useState('');
  const [leaveDate, setLeaveDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [leaveReason, setLeaveReason] = useState('');
  const [leaveReplacement, setLeaveReplacement] = useState('');
  const [leaveDayType, setLeaveDayType] = useState('full'); // 'full' | 'half'
  const [leaveHalfSession, setLeaveHalfSession] = useState('FN'); // 'FN' | 'AN'
  const [leaveLoading, setLeaveLoading] = useState(false);

  const [showInventory, setShowInventory] = useState(false);
  const [inventoryRequests, setInventoryRequests] = useState([]);
  const [inventoryItem, setInventoryItem] = useState('');
  const [inventoryQty, setInventoryQty] = useState(1);
  const [inventoryNotes, setInventoryNotes] = useState('');
  const [inventoryLoading, setInventoryLoading] = useState(false);

  // Daily Tally / Accounts state
  const [showTally, setShowTally] = useState(false);
  const [tallyDate, setTallyDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [tallyData, setTallyData] = useState(null); // { doctor_auto, nursing_auto, saved }
  const [tallyLoading, setTallyLoading] = useState(false);
  const [tallySubmitting, setTallySubmitting] = useState(false);
  // Manual inputs
  const [tallyPharmOp, setTallyPharmOp] = useState(0);
  const [tallyPharmEr, setTallyPharmEr] = useState(0);
  const [tallyPharmReview, setTallyPharmReview] = useState(0);
  // Per-nursing-service pharmacy counts {service_type: count}
  const [tallyPharmNursing, setTallyPharmNursing] = useState({});
  const [tallyConsCash, setTallyConsCash] = useState(0);
  const [tallyNursingCash, setTallyNursingCash] = useState(0);
  const [tallyNote, setTallyNote] = useState('');
  const [tallyFinalCons, setTallyFinalCons] = useState(0);
  const [tallyFinalNursing, setTallyFinalNursing] = useState(0);
  const [tallyFinalGrand, setTallyFinalGrand] = useState(0);
  // Edit-lock state
  const [tallyEditPwd, setTallyEditPwd] = useState('');
  const [tallyEditMode, setTallyEditMode] = useState(false);
  const [tallySignedBy, setTallySignedBy] = useState('');
  // Consultation fees for auto-compute (regular/review/er)
  const [tallyFees, setTallyFees] = useState({ regular_fee: 200, review_fee: 50, er_fee: 250 });

  // Fetch services on mount
  useEffect(() => {
    fetchServices();
    fetchTodayEntries();
    const interval = setInterval(fetchTodayEntries, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/nursing/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort by order field
      const sorted = response.data.sort((a, b) => (a.order || 99) - (b.order || 99));
      setServices(sorted);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    }
  };

  const fetchTodayEntries = async () => {
    try {
      const response = await axios.get(`${API}/nursing/entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };

  // Leave functions
  const fetchLeaveRequests = async () => {
    try {
      const response = await axios.get(`${API}/leave-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setLeaveRequests(response.data);
    } catch { /* ignore */ }
  };

  const submitLeaveRequest = async () => {
    if (!leaveName.trim() || !leaveDate) { toast.error('Staff name and date required'); return; }
    if (leaveDayType === 'half' && !['FN', 'AN'].includes(leaveHalfSession)) {
      toast.error('Pick FN or AN for half-day'); return;
    }
    setLeaveLoading(true);
    try {
      await axios.post(`${API}/leave-requests`, {
        staff_name: leaveName.trim(), leave_date: leaveDate,
        reason: leaveReason.trim(), replacement: leaveReplacement.trim(),
        day_type: leaveDayType,
        half_session: leaveDayType === 'half' ? leaveHalfSession : ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Leave request submitted');
      setLeaveName(''); setLeaveReason(''); setLeaveReplacement('');
      setLeaveDayType('full'); setLeaveHalfSession('FN');
      fetchLeaveRequests();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setLeaveLoading(false); }
  };

  // Inventory functions
  const fetchInventoryRequests = async () => {
    try {
      const response = await axios.get(`${API}/inventory-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setInventoryRequests(response.data);
    } catch { /* ignore */ }
  };

  const submitInventoryRequest = async () => {
    if (!inventoryItem.trim()) { toast.error('Item name required'); return; }
    setInventoryLoading(true);
    try {
      await axios.post(`${API}/inventory-requests`, {
        item_name: inventoryItem.trim(), quantity: inventoryQty,
        notes: inventoryNotes.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Inventory request submitted');
      setInventoryItem(''); setInventoryQty(1); setInventoryNotes('');
      fetchInventoryRequests();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setInventoryLoading(false); }
  };

  // Daily Tally / Accounts functions
  // Auto-compute consultation cash & final totals when pharmacy OP/ER/Review counts change
  useEffect(() => {
    const op = Number(tallyPharmOp) || 0;
    const er = Number(tallyPharmEr) || 0;
    const rev = Number(tallyPharmReview) || 0;
    const consCash = op * (tallyFees.regular_fee || 0) + er * (tallyFees.er_fee || 0) + rev * (tallyFees.review_fee || 0);
    setTallyConsCash(consCash);
    setTallyFinalCons(consCash);
    setTallyFinalGrand(consCash + (Number(tallyFinalNursing) || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tallyPharmOp, tallyPharmEr, tallyPharmReview, tallyFees]);

  // Auto-compute nursing cash from per-service pharmacy counts (excluding ECG)
  useEffect(() => {
    const autoSvc = tallyData?.nursing_auto?.services || {};
    let nursingCash = 0;
    Object.entries(tallyPharmNursing || {}).forEach(([k, ph]) => {
      if (k === 'ecg') return; // ECG excluded from net (matches backend net_revenue logic)
      const phCount = Number(ph) || 0;
      const sysSvc = autoSvc[k];
      // Derive effective rate: weighted avg from system data if available; else 0
      const rate = sysSvc && sysSvc.count
        ? (sysSvc.revenue / sysSvc.count)
        : 0;
      nursingCash += phCount * rate;
    });
    nursingCash = Math.round(nursingCash);
    setTallyNursingCash(nursingCash);
    setTallyFinalNursing(nursingCash);
    setTallyFinalGrand((Number(tallyFinalCons) || 0) + nursingCash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tallyPharmNursing, tallyData]);

  const openTally = async () => {
    setShowTally(true);
    setTallyEditMode(false);
    // Load latest consultation fee settings (for auto-compute)
    try {
      const r = await axios.get(`${API}/settings/consultation-fees`, { headers: { Authorization: `Bearer ${token}` } });
      setTallyFees({
        regular_fee: r.data.regular_fee || 200,
        review_fee: r.data.review_fee || 50,
        er_fee: r.data.er_fee || 250,
      });
    } catch (e) { /* keep defaults */ }
    await fetchTally(tallyDate);
  };

  const fetchTally = async (dateVal) => {
    setTallyLoading(true);
    try {
      const res = await axios.get(`${API}/daily-tally?date_str=${dateVal}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTallyData(res.data);
      // Pre-fill manual fields from saved (if any) or sensible defaults
      const saved = res.data.saved;
      if (saved) {
        setTallyPharmOp(saved.pharmacy_manual?.op_count ?? 0);
        setTallyPharmEr(saved.pharmacy_manual?.er_count ?? 0);
        setTallyPharmReview(saved.pharmacy_manual?.review_count ?? 0);
        setTallyPharmNursing(saved.pharmacy_manual?.nursing_services || {});
        setTallyConsCash(saved.cash_manual?.consultation_cash ?? 0);
        setTallyNursingCash(saved.cash_manual?.nursing_cash ?? 0);
        setTallyNote(saved.discrepancy_note || '');
        setTallyFinalCons(saved.final_override?.consultation_total ?? 0);
        setTallyFinalNursing(saved.final_override?.nursing_total ?? 0);
        setTallyFinalGrand(saved.final_override?.grand_total ?? 0);
        setTallySignedBy(saved.signed_by || '');
      } else {
        // No saved tally — leave manual fields BLANK so staff types fresh values
        setTallyPharmOp('');
        setTallyPharmEr('');
        setTallyPharmReview('');
        // Pre-fill pharmacy nursing-service counts from auto so nurse only adjusts mismatches
        const autoSvc = res.data.nursing_auto?.services || {};
        const initSvc = {};
        Object.keys(autoSvc).forEach(k => { initSvc[k] = autoSvc[k].count || 0; });
        setTallyPharmNursing(initSvc);
        setTallyConsCash(0);
        setTallyNursingCash(res.data.nursing_auto?.net_amount_excl_ecg ?? 0);
        setTallyNote('');
        setTallyFinalCons(0);
        setTallyFinalNursing(res.data.nursing_auto?.net_amount_excl_ecg ?? 0);
        setTallyFinalGrand(res.data.nursing_auto?.net_amount_excl_ecg ?? 0);
        setTallySignedBy('');
      }
    } catch (e) { toast.error('Failed to load tally'); }
    finally { setTallyLoading(false); }
  };

  const submitTally = async () => {
    if (!tallyData) return;
    if (!tallySignedBy.trim()) { toast.error('Signed by (staff name) is required'); return; }
    setTallySubmitting(true);
    try {
      await axios.post(`${API}/daily-tally`, {
        date: tallyDate,
        pharmacy_op_count: Number(tallyPharmOp) || 0,
        pharmacy_er_count: Number(tallyPharmEr) || 0,
        pharmacy_review_count: Number(tallyPharmReview) || 0,
        pharmacy_nursing_services: tallyPharmNursing,
        consultation_cash: Number(tallyConsCash) || 0,
        nursing_cash: Number(tallyNursingCash) || 0,
        discrepancy_note: tallyNote,
        final_consultation_total: Number(tallyFinalCons) || 0,
        final_nursing_total: Number(tallyFinalNursing) || 0,
        final_grand_total: Number(tallyFinalGrand) || 0,
        signed_by: tallySignedBy.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Daily tally submitted & locked');
      await fetchTally(tallyDate);
      setTallyEditMode(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit'); }
    finally { setTallySubmitting(false); }
  };

  const submitTallyEdit = async () => {
    if (!tallyData?.saved?.id) return;
    if (!tallyEditPwd.trim()) { toast.error('Admin password required'); return; }
    setTallySubmitting(true);
    try {
      await axios.put(`${API}/daily-tally/${tallyData.saved.id}`, {
        admin_password: tallyEditPwd,
        update: {
          date: tallyDate,
          pharmacy_op_count: Number(tallyPharmOp) || 0,
          pharmacy_er_count: Number(tallyPharmEr) || 0,
          pharmacy_review_count: Number(tallyPharmReview) || 0,
          pharmacy_nursing_services: tallyPharmNursing,
          consultation_cash: Number(tallyConsCash) || 0,
          nursing_cash: Number(tallyNursingCash) || 0,
          discrepancy_note: tallyNote,
          final_consultation_total: Number(tallyFinalCons) || 0,
          final_nursing_total: Number(tallyFinalNursing) || 0,
          final_grand_total: Number(tallyFinalGrand) || 0,
          signed_by: tallySignedBy.trim(),
        }
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tally updated');
      setTallyEditPwd('');
      setTallyEditMode(false);
      await fetchTally(tallyDate);
    } catch (error) { toast.error(error.response?.data?.detail || 'Edit failed'); }
    finally { setTallySubmitting(false); }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/nursing/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
      setShowSummary(true);
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.error('Failed to load summary');
    }
  };

  const fetchTodayRevenue = async () => {
    setRevenueLoading(true);
    try {
      const response = await axios.get(`${API}/finance/today-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayRevenue(response.data);
      setShowTodayRevenue(true);
    } catch (error) {
      console.error('Error fetching today revenue:', error);
      toast.error('Failed to load revenue data');
    } finally {
      setRevenueLoading(false);
    }
  };

  // Delete day functions
  const fetchNursingDatesSummary = async () => {
    setDeleteLoading(true);
    try {
      const response = await axios.get(`${API}/data/dates-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteDaySummary(response.data);
    } catch (error) {
      toast.error('Failed to load data summary');
    } finally {
      setDeleteLoading(false);
    }
  };

  const deleteNursingByDate = async (dateStr) => {
    try {
      const response = await axios.delete(`${API}/nursing/entries/by-date?date_str=${dateStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setDeleteConfirmDate(null);
      fetchNursingDatesSummary();
      fetchTodayEntries();
    } catch (error) {
      toast.error('Failed to delete nursing data');
    }
  };

  const verifyDeletePassword = async () => {
    setPasswordError('');
    try {
      await axios.post(`${API}/verify-delete-password`,
        { password: deletePassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordVerified(true);
      fetchNursingDatesSummary();
    } catch (error) {
      setPasswordError('Incorrect password');
    }
  };

  // Fetch history data based on tab
  const fetchHistoryData = async (tab, dateVal) => {
    setHistoryLoading(true);
    try {
      let response;
      if (tab === 'day') {
        response = await axios.get(`${API}/nursing/summary?date_str=${dateVal}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (tab === 'week') {
        response = await axios.get(`${API}/nursing/summary/weekly?week_start=${dateVal}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (tab === 'month') {
        response = await axios.get(`${API}/nursing/summary/monthly?month=${dateVal}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setHistoryData(response?.data || null);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load history data');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Change history date by days
  const changeHistoryDate = (days) => {
    const current = new Date(historyDate);
    current.setDate(current.getDate() + days);
    const newDate = current.toISOString().split('T')[0];
    setHistoryDate(newDate);
    fetchHistoryData('day', newDate);
  };

  // Get week start date (Monday)
  const getWeekStart = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  };

  const searchPatients = async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/nursing/patients/search?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching patients:', error);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchPatients(query);
  };

  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handlePatientTypeChange = (type) => {
    setPatientType(type);
    setSelectedPatient(null);
    setManualName('');
    setManualAge('');
    setManualSex('');
    setSearchQuery('');
    setSearchResults([]);
  };

  const toggleService = (serviceType) => {
    setSelectedServices(prev => ({
      ...prev,
      [serviceType]: !prev[serviceType]
    }));
  };

  const updateServiceOverride = (serviceType, cost) => {
    setServiceOverrides(prev => ({
      ...prev,
      [serviceType]: cost
    }));
  };

  const getServiceCost = (serviceType) => {
    // IM/IV are FREE for token patients
    if (patientType === 'token' && (serviceType === 'im' || serviceType === 'iv')) {
      return 0;
    }
    if (serviceOverrides[serviceType] !== undefined && serviceOverrides[serviceType] !== '') {
      return parseFloat(serviceOverrides[serviceType]) || 0;
    }
    const service = services.find(s => s.service_type === serviceType);
    return service ? service.default_cost : 0;
  };

  const calculateTotal = () => {
    return Object.keys(selectedServices)
      .filter(key => selectedServices[key])
      .reduce((sum, key) => sum + getServiceCost(key), 0);
  };

  const handleSubmitEntry = async () => {
    // Validate based on patient type
    if (patientType === 'token' && !selectedPatient) {
      toast.error('Please search and select a patient');
      return;
    }
    if ((patientType === 'walkin' || patientType === 'repeat') && !manualName.trim()) {
      toast.error('Please enter patient name');
      return;
    }
    
    const selectedServicesList = Object.keys(selectedServices)
      .filter(key => selectedServices[key])
      .map(key => {
        const service = services.find(s => s.service_type === key);
        return {
          service_type: key,
          name: service?.name || key,
          cost: getServiceCost(key),
          quantity: 1
        };
      });
    
    if (selectedServicesList.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    
    setLoading(true);
    try {
      const entryData = {
        patient_token: patientType === 'token' ? selectedPatient?.token_number : null,
        patient_name: patientType === 'token' ? (selectedPatient?.name || 'Unknown') : manualName,
        patient_age: patientType === 'token' ? selectedPatient?.age : manualAge,
        patient_sex: patientType === 'token' ? selectedPatient?.sex : manualSex,
        is_walkin: patientType === 'walkin',
        is_repeat: patientType === 'repeat',
        patient_type: patientType,
        services: selectedServicesList,
        notes: entryNotes || null
      };
      
      await axios.post(`${API}/nursing/entries`, entryData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Entry added successfully');
      
      // Reset form
      setSelectedPatient(null);
      setManualName('');
      setManualAge('');
      setManualSex('');
      setSelectedServices({});
      setServiceOverrides({});
      setEntryNotes('');
      setSearchQuery('');
      
      // Refresh entries
      fetchTodayEntries();
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Failed to add entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axios.delete(`${API}/nursing/entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchTodayEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleUpdateServiceCost = async (serviceType, newCost) => {
    try {
      await axios.put(`${API}/nursing/services/${serviceType}`, 
        { default_cost: parseFloat(newCost) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Service cost updated');
      fetchServices();
      setEditingService(null);
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
    }
  };

  // Filter services based on patient type
  const getFilteredServices = () => {
    // Show all services for all patient types
    return services;
  };

  // Get the cost for a service - IM/IV are free for token patients
  const getDisplayCost = (service) => {
    if (patientType === 'token' && (service.service_type === 'im' || service.service_type === 'iv')) {
      return 0; // Free for token patients
    }
    return service.default_cost;
  };

  // Group services by category
  const groupServicesByCategory = (serviceList) => {
    const groups = {
      injection: { title: patientType === 'token' ? 'Injections (FREE for Token Patients)' : 'Injections', services: [] },
      saline: { title: 'Saline/IV Fluids', services: [] },
      procedure: { title: 'Procedures', services: [] },
      diagnostic: { title: 'Diagnostic', services: [] },
      other: { title: 'Other Services', services: [] },
    };
    
    serviceList.forEach(service => {
      const category = service.category || 'other';
      if (groups[category]) {
        groups[category].services.push(service);
      } else {
        groups.other.services.push(service);
      }
    });
    
    return groups;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg sticky top-0 z-40">
        <div className="px-3 md:px-6 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-lg md:text-xl font-bold flex items-center gap-2">
                  <span className="text-2xl md:text-3xl font-black">K.M</span>
                  <span className="hidden md:inline text-sm font-medium opacity-90">Nursing Dashboard</span>
                </h1>
                <p className="text-xs text-purple-200 hidden md:block">Multi-Speciality Clinic, Koliyanur</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              <LiveClock />
              <Button
                onClick={fetchTodayRevenue}
                variant="ghost"
                data-testid="today-revenue-button"
                className="hover:bg-purple-500/30 text-white text-xs md:text-sm"
              >
                <Wallet className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Today's Revenue</span>
              </Button>
              <Button
                onClick={() => { setShowDeleteDay(true); fetchNursingDatesSummary(); }}
                variant="ghost"
                data-testid="delete-nursing-day-button"
                className="hover:bg-red-500/30 text-white text-xs md:text-sm"
              >
                <Trash2 className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Delete Day</span>
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                variant="ghost"
                data-testid="settings-button"
                className="hover:bg-purple-500/30 text-white text-xs md:text-sm"
              >
                <Settings className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Costs</span>
              </Button>
              <Button
                onClick={fetchSummary}
                variant="ghost"
                data-testid="summary-button"
                className="hover:bg-purple-500/30 text-white text-xs md:text-sm"
              >
                <Calculator className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Day End</span>
              </Button>
              <Button
                onClick={() => { setShowLeave(true); fetchLeaveRequests(); }}
                variant="ghost"
                data-testid="leave-button"
                className="hover:bg-orange-500/30 text-white text-xs md:text-sm"
              >
                <UserMinus className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Leave</span>
              </Button>
              <Button
                onClick={() => { setShowInventory(true); fetchInventoryRequests(); }}
                variant="ghost"
                data-testid="inventory-button"
                className="hover:bg-teal-500/30 text-white text-xs md:text-sm"
              >
                <Package className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Items</span>
              </Button>
              <Button
                onClick={openTally}
                variant="ghost"
                data-testid="accounts-button"
                className="hover:bg-emerald-500/30 text-white text-xs md:text-sm"
                title="Daily Accounts / Tally"
              >
                <Wallet className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Accounts</span>
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                data-testid="logout-button"
                className="hover:bg-red-500/30 text-white text-xs md:text-sm"
              >
                <LogOut className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="px-3 md:px-6 pb-2">
          <div className="flex gap-2">
            <Button
              onClick={() => setActiveTab('add')}
              variant={activeTab === 'add' ? 'default' : 'ghost'}
              className={`${activeTab === 'add' ? 'bg-white text-purple-700' : 'text-white hover:bg-purple-500/30'}`}
              data-testid="tab-add-entry"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
            <Button
              onClick={() => setActiveTab('entries')}
              variant={activeTab === 'entries' ? 'default' : 'ghost'}
              className={`${activeTab === 'entries' ? 'bg-white text-purple-700' : 'text-white hover:bg-purple-500/30'}`}
              data-testid="tab-entries"
            >
              <ListOrdered className="h-4 w-4 mr-2" />
              Today's Entries
              {todayEntries.length > 0 && (
                <span className="ml-2 bg-pink-500 text-white px-2 py-0.5 rounded-full text-xs">
                  {todayEntries.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        
        {/* ADD ENTRY TAB */}
        {activeTab === 'add' && (
          <Card className="p-4 md:p-6 shadow-lg border-2 border-purple-200">
            <h2 className="text-xl font-bold text-purple-700 mb-6 flex items-center gap-2">
              <UserPlus className="h-6 w-6" />
              Add Nursing Entry
            </h2>
            
            {/* Patient Type Selection - 3 Separate Checkboxes */}
            <div className="mb-6">
              <Label className="font-semibold text-purple-700 mb-3 block">Patient Type</Label>
              <div className="flex flex-wrap gap-4">
                <div 
                  onClick={() => handlePatientTypeChange('token')}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    patientType === 'token' 
                      ? 'border-purple-500 bg-purple-50 shadow-md' 
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                  data-testid="patient-type-token"
                >
                  <Checkbox checked={patientType === 'token'} />
                  <div>
                    <div className="font-bold text-purple-700">Token Patient</div>
                    <div className="text-xs text-gray-500">Today's registered patient</div>
                  </div>
                </div>
                
                <div 
                  onClick={() => handlePatientTypeChange('walkin')}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    patientType === 'walkin' 
                      ? 'border-orange-500 bg-orange-50 shadow-md' 
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                  data-testid="patient-type-walkin"
                >
                  <Checkbox checked={patientType === 'walkin'} />
                  <div>
                    <div className="font-bold text-orange-700">Walk-in</div>
                    <div className="text-xs text-gray-500">New patient, not registered</div>
                  </div>
                </div>
                
                <div 
                  onClick={() => handlePatientTypeChange('repeat')}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    patientType === 'repeat' 
                      ? 'border-blue-500 bg-blue-50 shadow-md' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  data-testid="patient-type-repeat"
                >
                  <Checkbox checked={patientType === 'repeat'} />
                  <div>
                    <div className="font-bold text-blue-700">Repeat</div>
                    <div className="text-xs text-gray-500">Follow-up (e.g., 3-day IV)</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Patient Details Entry */}
            <div className="mb-6">
              {patientType === 'token' ? (
                <div className="relative">
                  <Label className="font-semibold text-purple-700 mb-2 block">Search Patient</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by token (P-001, G-001) or name..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="pl-10 h-12 text-lg"
                      data-testid="patient-search-input"
                    />
                  </div>
                  
                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {searchResults.map((patient) => (
                        <div
                          key={patient.id}
                          onClick={() => selectPatient(patient)}
                          className="p-3 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-purple-600 text-lg">{patient.token_number}</span>
                            <span className="font-medium">{patient.name || 'No name'}</span>
                            <span className="text-sm text-gray-500">{patient.age} {patient.sex}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Selected Patient */}
                  {selectedPatient && (
                    <div className="mt-3 p-4 bg-purple-100 rounded-lg border-2 border-purple-300 flex items-center justify-between">
                      <div>
                        <span className="font-black text-purple-700 text-2xl">{selectedPatient.token_number}</span>
                        <span className="ml-4 font-semibold text-lg">{selectedPatient.name || 'No name'}</span>
                        <span className="ml-3 text-gray-600">{selectedPatient.age} {selectedPatient.sex}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedPatient(null)}>
                        <X className="h-5 w-5" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <Label className="font-semibold text-purple-700 mb-2 block">
                    {patientType === 'walkin' ? 'Walk-in Patient Details' : 'Repeat Patient Details'}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-gray-600">Name *</Label>
                      <Input
                        value={manualName}
                        onChange={(e) => setManualName(e.target.value)}
                        placeholder="Patient name"
                        className="h-12 text-lg"
                        data-testid="manual-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Age</Label>
                      <Input
                        value={manualAge}
                        onChange={(e) => setManualAge(e.target.value)}
                        placeholder="e.g., 35"
                        className="h-12 text-lg"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Sex</Label>
                      <select
                        value={manualSex}
                        onChange={(e) => setManualSex(e.target.value)}
                        className="w-full h-12 px-3 border rounded-md text-lg"
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Services Selection */}
            <div className="mb-6">
              <Label className="font-semibold text-purple-700 mb-3 block">Select Services</Label>
              
              {/* IM/IV Notice for Token patients */}
              {patientType === 'token' && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  <strong>Note:</strong> IM/IV injections are <strong>FREE</strong> for token patients (included in consultation).
                </div>
              )}
              
              {(() => {
                const filteredServices = getFilteredServices();
                const groups = groupServicesByCategory(filteredServices);
                
                return Object.entries(groups).map(([category, group]) => {
                  if (group.services.length === 0) return null;
                  
                  return (
                    <div key={category} className="mb-4">
                      <div className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        {group.title}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {group.services.filter(s => s.is_active).map((service) => {
                          const Icon = SERVICE_ICONS[service.service_type] || Stethoscope;
                          const isSelected = selectedServices[service.service_type];
                          const isFreeForToken = patientType === 'token' && (service.service_type === 'im' || service.service_type === 'iv');
                          const displayCost = isFreeForToken ? 0 : service.default_cost;
                          
                          return (
                            <div
                              key={service.service_type}
                              onClick={() => toggleService(service.service_type)}
                              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                isSelected 
                                  ? isFreeForToken 
                                    ? 'border-green-500 bg-green-50 shadow-md'
                                    : 'border-purple-500 bg-purple-50 shadow-md' 
                                  : 'border-gray-200 hover:border-purple-300'
                              }`}
                              data-testid={`service-${service.service_type}`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Checkbox checked={isSelected} />
                                <Icon className={`h-5 w-5 ${isSelected ? (isFreeForToken ? 'text-green-600' : 'text-purple-600') : 'text-gray-400'}`} />
                              </div>
                              <div className="font-medium text-sm">{service.name}</div>
                              <div className="flex items-center gap-1 mt-1">
                                {isFreeForToken ? (
                                  <span className="text-sm font-bold text-green-600">FREE</span>
                                ) : (
                                  <>
                                    <span className="text-xs text-gray-500">₹</span>
                                    {isSelected ? (
                                      <Input
                                        value={serviceOverrides[service.service_type] ?? service.default_cost}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          updateServiceOverride(service.service_type, e.target.value);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-7 w-20 text-sm px-2"
                                        type="number"
                                      />
                                    ) : (
                                      <span className="text-sm font-bold text-purple-600">{displayCost}</span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            
            {/* Notes */}
            <div className="mb-6">
              <Label className="text-sm text-gray-600">Notes (optional)</Label>
              <Input
                value={entryNotes}
                onChange={(e) => setEntryNotes(e.target.value)}
                placeholder="Any additional notes..."
                className="h-12"
              />
            </div>
            
            {/* Total & Submit */}
            <div className="flex items-center justify-between bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-6">
              <div>
                <span className="text-sm text-gray-600">Total Amount:</span>
                <span className="text-4xl font-black text-purple-700 ml-3">₹{calculateTotal()}</span>
              </div>
              <Button
                onClick={handleSubmitEntry}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 h-14 text-lg font-bold rounded-xl"
                data-testid="add-entry-button"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Add Entry
              </Button>
            </div>
          </Card>
        )}

        {/* TODAY'S ENTRIES TAB */}
        {activeTab === 'entries' && (
          <Card className="p-4 md:p-6 shadow-lg border-2 border-pink-200">
            {/* Period Selection Tabs */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                onClick={() => { setHistoryTab('today'); setHistoryData(null); }}
                variant={historyTab === 'today' ? 'default' : 'outline'}
                size="sm"
                className={historyTab === 'today' ? 'bg-pink-600 hover:bg-pink-700' : ''}
              >
                Today
              </Button>
              <Button
                onClick={() => { setHistoryTab('day'); fetchHistoryData('day', historyDate); }}
                variant={historyTab === 'day' ? 'default' : 'outline'}
                size="sm"
                className={historyTab === 'day' ? 'bg-pink-600 hover:bg-pink-700' : ''}
              >
                Any Day
              </Button>
              <Button
                onClick={() => { 
                  setHistoryTab('week'); 
                  const weekStart = getWeekStart(new Date().toISOString().split('T')[0]);
                  setHistoryWeekStart(weekStart);
                  fetchHistoryData('week', weekStart); 
                }}
                variant={historyTab === 'week' ? 'default' : 'outline'}
                size="sm"
                className={historyTab === 'week' ? 'bg-pink-600 hover:bg-pink-700' : ''}
              >
                Weekly
              </Button>
              <Button
                onClick={() => { setHistoryTab('month'); fetchHistoryData('month', historyMonth); }}
                variant={historyTab === 'month' ? 'default' : 'outline'}
                size="sm"
                className={historyTab === 'month' ? 'bg-pink-600 hover:bg-pink-700' : ''}
              >
                Monthly
              </Button>
              
              {/* Summary Button */}
              <Button
                onClick={fetchSummary}
                variant="outline"
                size="sm"
                className="ml-auto border-green-500 text-green-600 hover:bg-green-50"
              >
                <BarChart3 className="h-4 w-4 mr-1" />
                Day End Summary
              </Button>
            </div>

            {/* Date Navigation for Day/Week/Month */}
            {historyTab === 'day' && (
              <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                <Button variant="outline" size="sm" onClick={() => changeHistoryDate(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-pink-600" />
                  <Input
                    type="date"
                    value={historyDate}
                    onChange={(e) => {
                      setHistoryDate(e.target.value);
                      fetchHistoryData('day', e.target.value);
                    }}
                    className="w-40 h-8 text-center font-bold"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={() => changeHistoryDate(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {historyTab === 'week' && (
              <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                <Button variant="outline" size="sm" onClick={() => {
                  const d = new Date(historyWeekStart);
                  d.setDate(d.getDate() - 7);
                  const newWeek = d.toISOString().split('T')[0];
                  setHistoryWeekStart(newWeek);
                  fetchHistoryData('week', newWeek);
                }}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-pink-600" />
                  <span className="font-bold text-pink-700">
                    Week of {historyWeekStart && new Date(historyWeekStart).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <Button variant="outline" size="sm" onClick={() => {
                  const d = new Date(historyWeekStart);
                  d.setDate(d.getDate() + 7);
                  const newWeek = d.toISOString().split('T')[0];
                  setHistoryWeekStart(newWeek);
                  fetchHistoryData('week', newWeek);
                }}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {historyTab === 'month' && (
              <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                <Button variant="outline" size="sm" onClick={() => {
                  const [y, m] = historyMonth.split('-').map(Number);
                  const newDate = m === 1 ? `${y-1}-12` : `${y}-${String(m-1).padStart(2, '0')}`;
                  setHistoryMonth(newDate);
                  fetchHistoryData('month', newDate);
                }}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-pink-600" />
                  <Input
                    type="month"
                    value={historyMonth}
                    onChange={(e) => {
                      setHistoryMonth(e.target.value);
                      fetchHistoryData('month', e.target.value);
                    }}
                    className="w-40 h-8 text-center font-bold"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={() => {
                  const [y, m] = historyMonth.split('-').map(Number);
                  const newDate = m === 12 ? `${y+1}-01` : `${y}-${String(m+1).padStart(2, '0')}`;
                  setHistoryMonth(newDate);
                  fetchHistoryData('month', newDate);
                }}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* History Data Display */}
            {historyTab !== 'today' && historyLoading && (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            )}

            {historyTab !== 'today' && !historyLoading && historyData && (
              <div className="mb-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gradient-to-r from-green-100 to-emerald-100 p-4 rounded-xl text-center">
                    <div className="text-3xl font-black text-green-700">₹{historyData.total_revenue || 0}</div>
                    <div className="text-sm text-green-600">Total Revenue</div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 rounded-xl text-center">
                    <div className="text-3xl font-black text-purple-700">{historyData.total_entries || 0}</div>
                    <div className="text-sm text-purple-600">Patients Served</div>
                  </div>
                  {historyTab === 'week' && historyData.daily_breakdown && (
                    <div className="bg-gradient-to-r from-blue-100 to-cyan-100 p-4 rounded-xl text-center">
                      <div className="text-3xl font-black text-blue-700">{Object.keys(historyData.daily_breakdown).length}</div>
                      <div className="text-sm text-blue-600">Days with Entries</div>
                    </div>
                  )}
                </div>

                {/* Service Breakdown */}
                {historyData.services && Object.keys(historyData.services).length > 0 && (
                  <div className="bg-white rounded-lg border p-4">
                    <h3 className="font-bold text-gray-700 mb-3">Service Breakdown</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(historyData.services).map(([type, data]) => {
                        const Icon = SERVICE_ICONS[type] || Stethoscope;
                        return (
                          <div key={type} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-purple-600" />
                              <span className="text-sm">{data.name}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-xs bg-purple-100 text-purple-700 px-1 rounded mr-2">{data.count}</span>
                              <span className="font-bold text-green-600">₹{data.revenue}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Daily breakdown for week/month */}
                {(historyTab === 'week' || historyTab === 'month') && historyData.daily_breakdown && Object.keys(historyData.daily_breakdown).length > 0 && (
                  <div className="bg-white rounded-lg border p-4 mt-4">
                    <h3 className="font-bold text-gray-700 mb-3">Daily Breakdown</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {Object.entries(historyData.daily_breakdown).sort().map(([dateStr, data]) => (
                        <div key={dateStr} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="font-medium">{new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">{data.entries} patients</span>
                            <span className="font-bold text-green-600">₹{data.revenue}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Today's Header */}
            {historyTab === 'today' && (
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-pink-700 flex items-center gap-2">
                  <ClipboardList className="h-6 w-6" />
                  Today's Nursing Entries
                </h2>
                <div className="flex items-center gap-4">
                  <span className="bg-pink-100 text-pink-700 px-4 py-2 rounded-full font-bold">
                    {todayEntries.length} entries
                  </span>
                  <span className="text-2xl font-black text-green-600">
                    ₹{todayEntries.reduce((sum, e) => sum + e.total_cost, 0)}
                  </span>
                </div>
              </div>
            )}
            
            {/* Today's Entries List */}
            {historyTab === 'today' && todayEntries.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <ClipboardList className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-xl">No entries yet today</p>
                <p className="text-sm mt-2">Switch to "Add Entry" tab to create a new entry</p>
              </div>
            ) : historyTab === 'today' && (
              <div className="space-y-4">
                {todayEntries.map((entry) => (
                  <div 
                    key={entry.id}
                    className="p-4 bg-white rounded-xl border-2 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {entry.patient_token && (
                            <span className="font-black text-purple-600 text-xl">{entry.patient_token}</span>
                          )}
                          <span className="font-semibold text-lg">{entry.patient_name}</span>
                          {entry.patient_age && (
                            <span className="text-gray-500">{entry.patient_age} {entry.patient_sex}</span>
                          )}
                          <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                            entry.patient_type === 'token' ? 'bg-purple-100 text-purple-700' :
                            entry.patient_type === 'walkin' ? 'bg-orange-100 text-orange-700' :
                            entry.patient_type === 'repeat' ? 'bg-blue-100 text-blue-700' :
                            entry.is_walkin ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {entry.patient_type === 'token' ? 'Token' :
                             entry.patient_type === 'walkin' ? 'Walk-in' :
                             entry.patient_type === 'repeat' ? 'Repeat' :
                             entry.is_walkin ? 'Walk-in' : 'Token'}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-2">
                          {entry.services.map((s, idx) => (
                            <span 
                              key={idx}
                              className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium"
                            >
                              {s.name}: ₹{s.cost}
                            </span>
                          ))}
                        </div>
                        
                        {entry.notes && (
                          <p className="text-sm text-gray-500 italic">{entry.notes}</p>
                        )}
                        
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(entry.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-4 ml-4">
                        <span className="text-2xl font-black text-green-600">₹{entry.total_cost}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-10 w-10"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-purple-700 flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Service Cost Settings
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {services.map((service) => {
                  const Icon = SERVICE_ICONS[service.service_type] || Stethoscope;
                  const isEditing = editingService === service.service_type;
                  
                  return (
                    <div 
                      key={service.service_type}
                      className="flex items-center justify-between p-3 bg-purple-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-purple-600" />
                        <span className="font-medium">{service.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isEditing ? (
                          <>
                            <Input
                              value={editCost}
                              onChange={(e) => setEditCost(e.target.value)}
                              className="w-24 h-8"
                              type="number"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              onClick={() => handleUpdateServiceCost(service.service_type, editCost)}
                              className="h-8 bg-green-600 hover:bg-green-700"
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingService(null)}
                              className="h-8"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="font-bold text-purple-700">₹{service.default_cost}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingService(service.service_type);
                                setEditCost(service.default_cost.toString());
                              }}
                              className="h-8"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <Button
                onClick={() => setShowSettings(false)}
                className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
              >
                Done
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Day End Summary Modal */}
      {showSummary && summary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg bg-white shadow-2xl">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-green-700 flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Day End Revenue Summary
                </h2>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 text-xs border-green-500 text-green-700" onClick={() => printDayEndReport(token, summary.date)} data-testid="print-day-end-summary">
                    <Printer className="h-4 w-4 mr-1" /> Print
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowSummary(false)}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              
              {/* Total Revenue */}
              <div className="text-center bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-6 mb-4">
                <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                <p className="text-5xl font-black text-green-700">₹{summary.total_revenue}</p>
                <p className="text-sm text-gray-500 mt-2">{summary.total_entries} patients served</p>
              </div>
              
              {/* Service Breakdown */}
              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                <h3 className="font-semibold text-gray-700">Service Breakdown</h3>
                {Object.keys(summary.services).length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No services recorded today</p>
                ) : (
                  Object.entries(summary.services).map(([type, data]) => {
                    const Icon = SERVICE_ICONS[type] || Stethoscope;
                    return (
                      <div 
                        key={type}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-purple-600" />
                          <span className="font-medium">{data.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {data.count} done
                          </span>
                          <span className="font-bold text-green-600">₹{data.revenue}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              
              <Button
                onClick={() => setShowSummary(false)}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Today's Revenue Modal (Nurse - Today Only) */}
      {showTodayRevenue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Wallet className="h-6 w-6 text-purple-700" />
                  <h2 className="text-lg font-bold text-purple-900" data-testid="today-revenue-title">Today's Revenue</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowTodayRevenue(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {revenueLoading && <div className="text-center py-8 text-gray-500">Loading...</div>}

              {!revenueLoading && todayRevenue && (
                <div className="space-y-4">
                  {/* Grand Total */}
                  <div className="bg-gradient-to-r from-purple-700 to-pink-600 rounded-xl p-4 text-white text-center shadow-lg" data-testid="nurse-grand-total">
                    <div className="text-xs font-medium opacity-80 uppercase tracking-wider">Net Collection Today</div>
                    <div className="text-4xl font-black mt-1">₹{(todayRevenue.doctor_revenue?.total_revenue || 0) + (todayRevenue.nursing_revenue?.net_revenue || 0)}</div>
                    <div className="text-xs opacity-70 mt-1">{todayRevenue.date} (excl. ECG)</div>
                  </div>

                  {/* Doctor Revenue */}
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200" data-testid="nurse-doctor-revenue">
                    <div className="flex items-center gap-2 mb-3">
                      <Receipt className="h-5 w-5 text-red-800" />
                      <span className="font-bold text-red-900 text-sm">Doctor Consultation</span>
                    </div>
                    <div className="text-2xl font-black text-red-900">₹{todayRevenue.doctor_revenue?.total_revenue || 0}</div>
                    <div className="text-xs text-red-700 mt-1">{todayRevenue.doctor_revenue?.total_patients || 0} patients</div>
                    <div className="mt-3 space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">Regular (₹{todayRevenue.fee_settings?.regular_fee})</span>
                        <span className="font-bold text-red-800">{todayRevenue.doctor_revenue?.regular_count || 0}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">Review (₹{todayRevenue.fee_settings?.review_fee})</span>
                        <span className="font-bold text-amber-700">{todayRevenue.doctor_revenue?.review_count || 0}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">ER (₹{todayRevenue.fee_settings?.er_fee})</span>
                        <span className="font-bold text-orange-700">{todayRevenue.doctor_revenue?.er_count || 0}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">Exempted (₹0)</span>
                        <span className="font-bold text-gray-500">{todayRevenue.doctor_revenue?.exempted_count || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Nursing Revenue */}
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-200" data-testid="nurse-nursing-revenue">
                    <div className="flex items-center gap-2 mb-3">
                      <Syringe className="h-5 w-5 text-purple-700" />
                      <span className="font-bold text-purple-900 text-sm">Nursing Services</span>
                    </div>
                    <div className="text-2xl font-black text-purple-900">₹{todayRevenue.nursing_revenue?.net_revenue || 0}</div>
                    <div className="text-xs text-purple-700 mt-1">{todayRevenue.nursing_revenue?.total_entries || 0} entries (excl. ECG ₹{todayRevenue.nursing_revenue?.ecg_revenue || 0})</div>
                    {todayRevenue.nursing_revenue?.services && Object.keys(todayRevenue.nursing_revenue.services).length > 0 && (
                      <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                        {Object.entries(todayRevenue.nursing_revenue.services).map(([key, svc]) => (
                          <div key={key} className={`flex justify-between items-center text-xs ${key === 'ecg' ? 'opacity-50 line-through' : ''}`}>
                            <span className="text-gray-600 truncate mr-2">{svc.name} {key === 'ecg' ? '(excluded)' : ''}</span>
                            <span className="font-bold text-purple-700 whitespace-nowrap">₹{svc.revenue}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => setShowTodayRevenue(false)}
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Nursing Day Modal */}
      {showDeleteDay && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-md bg-white shadow-2xl">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Trash2 className="h-6 w-6 text-red-600" />
                  <h2 className="text-lg font-bold text-red-700" data-testid="delete-nursing-title">Delete Nursing Data</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShowDeleteDay(false); setDeleteConfirmDate(null); setPasswordVerified(false); setDeletePassword(''); setPasswordError(''); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Password Gate */}
              {!passwordVerified ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">Enter master password to access deletion</p>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && verifyDeletePassword()}
                    placeholder="Master password"
                    className="w-full h-10 border rounded-lg px-3 text-sm"
                    data-testid="delete-password-input"
                  />
                  {passwordError && <p className="text-xs text-red-600 font-medium">{passwordError}</p>}
                  <Button
                    onClick={verifyDeletePassword}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    data-testid="verify-password-btn"
                  >
                    Verify Password
                  </Button>
                </div>
              ) : (
                <>
                  {deleteLoading && <div className="text-center py-6 text-gray-500">Loading...</div>}

                  {!deleteLoading && deleteDaySummary && (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {deleteDaySummary.nursing_dates.length === 0 ? (
                        <p className="text-center text-gray-500 py-6">No nursing data found</p>
                      ) : (
                        deleteDaySummary.nursing_dates.map(item => (
                          <div key={item.date} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div>
                              <span className="font-bold text-gray-800">
                                {new Date(item.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                              <span className="text-sm text-gray-500 ml-2">({item.count} entries)</span>
                            </div>
                            {deleteConfirmDate === item.date ? (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 text-white text-xs h-8"
                                  onClick={() => deleteNursingByDate(item.date)}
                                  data-testid={`confirm-delete-nursing-${item.date}`}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs h-8"
                                  onClick={() => setDeleteConfirmDate(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50 text-xs h-8"
                                onClick={() => setDeleteConfirmDate(item.date)}
                                data-testid={`delete-nursing-btn-${item.date}`}
                              >
                                <Trash2 className="h-3 w-3 mr-1" /> Delete
                              </Button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}

              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => { setShowDeleteDay(false); setDeleteConfirmDate(null); setPasswordVerified(false); setDeletePassword(''); setPasswordError(''); }}
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Leave Request Modal */}
      {showLeave && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-md bg-white shadow-2xl my-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <UserMinus className="h-5 w-5 text-orange-600" />
                <h2 className="text-base font-bold text-gray-900" data-testid="leave-modal-title">Staff Leave & Duty Replace</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowLeave(false)}><X className="h-5 w-5" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Submit form */}
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-orange-700">Staff Name *</label>
                    <input value={leaveName} onChange={(e) => setLeaveName(e.target.value)} placeholder="Name"
                      className="w-full h-8 rounded-md border px-2 text-sm" data-testid="leave-name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-orange-700">Leave Date *</label>
                    <input type="date" value={leaveDate} onChange={(e) => setLeaveDate(e.target.value)}
                      className="w-full h-8 rounded-md border px-2 text-sm" data-testid="leave-date-input" />
                  </div>
                </div>
                {/* Full / Half Day selector */}
                <div>
                  <label className="text-xs font-medium text-orange-700 block mb-1">Duration</label>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setLeaveDayType('full')}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        leaveDayType === 'full' ? 'bg-orange-600 text-white border-orange-600 shadow-sm' : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50'
                      }`}
                      data-testid="leave-day-full"
                    >
                      Full Day
                    </button>
                    <button
                      type="button"
                      onClick={() => setLeaveDayType('half')}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        leaveDayType === 'half' ? 'bg-orange-600 text-white border-orange-600 shadow-sm' : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50'
                      }`}
                      data-testid="leave-day-half"
                    >
                      Half Day
                    </button>
                    {leaveDayType === 'half' && (
                      <div className="flex items-center gap-1 ml-1 pl-2 border-l border-orange-300">
                        <button
                          type="button"
                          onClick={() => setLeaveHalfSession('FN')}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                            leaveHalfSession === 'FN' ? 'bg-amber-500 text-white border-amber-500 shadow-sm' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                          }`}
                          data-testid="leave-session-fn"
                          title="Forenoon"
                        >
                          FN
                        </button>
                        <button
                          type="button"
                          onClick={() => setLeaveHalfSession('AN')}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                            leaveHalfSession === 'AN' ? 'bg-amber-500 text-white border-amber-500 shadow-sm' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                          }`}
                          data-testid="leave-session-an"
                          title="Afternoon"
                        >
                          AN
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-orange-700">Reason</label>
                    <input value={leaveReason} onChange={(e) => setLeaveReason(e.target.value)} placeholder="Reason"
                      className="w-full h-8 rounded-md border px-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-orange-700">Replacement</label>
                    <input value={leaveReplacement} onChange={(e) => setLeaveReplacement(e.target.value)} placeholder="Who covers"
                      className="w-full h-8 rounded-md border px-2 text-sm" />
                  </div>
                </div>
                <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white h-8 text-sm" onClick={submitLeaveRequest} disabled={leaveLoading} data-testid="submit-leave-button">
                  {leaveLoading ? '...' : 'Submit Leave Request'}
                </Button>
              </div>

              {/* History */}
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-gray-500 uppercase">Recent Requests</h4>
                {leaveRequests.length === 0 ? <p className="text-sm text-gray-400 text-center py-3">No requests</p> : (
                  leaveRequests.slice(0, 15).map(r => (
                    <div key={r.id} className={`flex items-center justify-between p-2 rounded-lg border text-xs ${
                      r.status === 'approved' ? 'bg-green-50 border-green-200' : r.status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div>
                        <span className="font-bold text-gray-800">{r.staff_name}</span>
                        <span className="text-gray-500 ml-2">{r.leave_date}</span>
                        <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                          r.day_type === 'half' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {r.day_type === 'half' ? `Half · ${r.half_session || ''}` : 'Full'}
                        </span>
                        {r.replacement && <span className="text-blue-600 ml-1">→ {r.replacement}</span>}
                      </div>
                      <span className={`font-bold uppercase text-xs px-1.5 py-0.5 rounded ${
                        r.status === 'approved' ? 'text-green-700 bg-green-100' : r.status === 'rejected' ? 'text-red-700 bg-red-100' : 'text-yellow-700 bg-yellow-100'
                      }`}>{r.status}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Inventory Request Modal */}
      {showInventory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-md bg-white shadow-2xl my-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-teal-600" />
                <h2 className="text-base font-bold text-gray-900" data-testid="inventory-modal-title">Inventory Request</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowInventory(false)}><X className="h-5 w-5" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Submit form */}
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-teal-700">Item Name *</label>
                    <input value={inventoryItem} onChange={(e) => setInventoryItem(e.target.value)} placeholder="e.g., Gloves, Syringes..."
                      className="w-full h-8 rounded-md border px-2 text-sm" data-testid="inventory-item-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-teal-700">Qty</label>
                    <input type="number" min="1" value={inventoryQty} onChange={(e) => setInventoryQty(parseInt(e.target.value) || 1)}
                      className="w-full h-8 rounded-md border px-2 text-sm text-center" data-testid="inventory-qty-input" />
                  </div>
                </div>
                <input value={inventoryNotes} onChange={(e) => setInventoryNotes(e.target.value)} placeholder="Notes (optional)"
                  className="w-full h-8 rounded-md border px-2 text-sm" />
                <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white h-8 text-sm" onClick={submitInventoryRequest} disabled={inventoryLoading} data-testid="submit-inventory-button">
                  {inventoryLoading ? '...' : 'Request Item'}
                </Button>
              </div>

              {/* History */}
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-gray-500 uppercase">Recent Requests</h4>
                {inventoryRequests.length === 0 ? <p className="text-sm text-gray-400 text-center py-3">No requests</p> : (
                  inventoryRequests.slice(0, 15).map(r => (
                    <div key={r.id} className={`flex items-center justify-between p-2 rounded-lg border text-xs ${
                      r.status === 'approved' ? 'bg-green-50 border-green-200' : r.status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div>
                        <span className="font-bold text-gray-800">{r.item_name}</span>
                        <span className="text-gray-500 ml-1">x{r.quantity}</span>
                        {r.notes && <span className="text-gray-400 ml-1">({r.notes})</span>}
                      </div>
                      <span className={`font-bold uppercase text-xs px-1.5 py-0.5 rounded ${
                        r.status === 'approved' ? 'text-green-700 bg-green-100' : r.status === 'rejected' ? 'text-red-700 bg-red-100' : 'text-yellow-700 bg-yellow-100'
                      }`}>{r.status}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Daily Tally / Accounts Modal */}
      {showTally && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 overflow-y-auto">
          <Card className="w-full max-w-3xl bg-white shadow-2xl my-4 max-h-[92vh] overflow-hidden flex flex-col" data-testid="tally-modal">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-emerald-50 to-teal-50">
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-emerald-700" />
                <h2 className="text-base font-bold text-gray-900">Daily Accounts · Tally</h2>
                {tallyData?.saved?.locked && (
                  <span className="text-[10px] font-bold text-white bg-emerald-600 px-2 py-0.5 rounded uppercase">Locked</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={tallyDate}
                  onChange={(e) => { setTallyDate(e.target.value); fetchTally(e.target.value); setTallyEditMode(false); }}
                  className="h-8 rounded-md border px-2 text-sm"
                  data-testid="tally-date-picker"
                />
                <Button variant="outline" size="sm" className="h-8 text-xs border-emerald-500 text-emerald-700" onClick={() => printDayEndReport(token, tallyDate)} data-testid="print-tally">
                  <Printer className="h-4 w-4 mr-1" /> {tallyData?.saved?.locked ? 'Print' : 'Print Draft'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowTally(false)} data-testid="close-tally"><X className="h-5 w-5" /></Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {tallyLoading ? (
                <div className="text-center py-10 text-gray-500">Loading…</div>
              ) : (
                <>
                  {/* DOCTOR-SIDE AUTO */}
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-3 py-2 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Stethoscope className="h-4 w-4 text-red-700" />
                        <span className="font-semibold text-sm text-slate-800">Doctor — auto from system</span>
                      </div>
                      <span className="text-xs text-slate-500">read-only</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 text-xs">
                      <Stat label="OP" value={tallyData?.doctor_auto?.op_count ?? 0} />
                      <Stat label="ER" value={tallyData?.doctor_auto?.er_count ?? 0} />
                      <Stat label="Review" value={tallyData?.doctor_auto?.review_count ?? 0} />
                      <Stat label="Exempt" value={tallyData?.doctor_auto?.exempted_count ?? 0} />
                      <Stat label="Total Cases" value={tallyData?.doctor_auto?.total_cases ?? 0} highlight />
                    </div>
                    <div className="px-3 pb-2 text-right text-sm">
                      <span className="text-slate-500">Consultation revenue: </span>
                      <span className="font-bold text-emerald-700 tabular-nums">₹ {Number(tallyData?.doctor_auto?.total_amount || 0).toFixed(0)}</span>
                    </div>
                  </div>

                  {/* NURSING-SIDE AUTO */}
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-3 py-2 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Syringe className="h-4 w-4 text-purple-700" />
                        <span className="font-semibold text-sm text-slate-800">Nursing — auto from system</span>
                      </div>
                      <span className="text-xs text-slate-500">read-only</span>
                    </div>
                    {Object.keys(tallyData?.nursing_auto?.services || {}).length === 0 ? (
                      <p className="text-xs text-gray-400 italic p-3">No nursing entries today.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead className="bg-purple-50 text-purple-900">
                            <tr>
                              <th className="px-2 py-1 text-left">Service</th>
                              <th className="px-2 py-1 text-center">Count</th>
                              <th className="px-2 py-1 text-right">Revenue (₹)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(tallyData.nursing_auto.services).map(([k, v]) => (
                              <React.Fragment key={k}>
                                <tr className="border-t bg-purple-50/40">
                                  <td className="px-2 py-1 font-semibold">{v.name || k}</td>
                                  <td className="px-2 py-1 text-center tabular-nums">{v.count}</td>
                                  <td className="px-2 py-1 text-right tabular-nums">{Number(v.revenue).toFixed(0)}</td>
                                </tr>
                                {/* Cost breakdown when there are multiple rates */}
                                {(v.cost_breakdown || []).length > 1 && (v.cost_breakdown).map((b, idx) => (
                                  <tr key={`auto-${k}-${idx}`} className="border-t border-dashed bg-white">
                                    <td className="px-2 py-0.5 pl-6 text-slate-600 italic text-[11px]">↳ ₹{Math.round(b.cost)} rate</td>
                                    <td className="px-2 py-0.5 text-center text-[11px] tabular-nums">{b.count}</td>
                                    <td className="px-2 py-0.5 text-right text-[11px] tabular-nums">{Math.round(b.revenue)}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2 p-3 text-xs">
                      <Stat label="Total entries" value={tallyData?.nursing_auto?.total_entries ?? 0} />
                      <Stat label="Gross" value={`₹ ${Number(tallyData?.nursing_auto?.total_amount || 0).toFixed(0)}`} />
                      <Stat label="Net (excl ECG)" value={`₹ ${Number(tallyData?.nursing_auto?.net_amount_excl_ecg || 0).toFixed(0)}`} highlight />
                    </div>
                  </div>

                  {/* PHARMACY MANUAL */}
                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-3">
                    <div className="flex items-center gap-2">
                      <Pill className="h-4 w-4 text-amber-700" />
                      <span className="font-semibold text-sm text-amber-900">Pharmacy reconciliation — manual entry</span>
                    </div>
                    <p className="text-[11px] text-amber-700">Enter what pharmacy actually billed. Mismatch with system counts will be visible inline.</p>

                    {/* Consultation cases */}
                    <div>
                      <div className="text-[11px] font-bold text-amber-800 uppercase mb-1 tracking-wider flex items-center justify-between">
                        <span>Consultation cases</span>
                        <span className="text-[10px] font-normal text-amber-600 normal-case tracking-normal">
                          Auto-total: <b className="tabular-nums text-amber-900">₹ {Math.round(tallyConsCash || 0)}</b>
                          <span className="ml-1 text-amber-500">
                            (Reg ₹{tallyFees.regular_fee} · ER ₹{tallyFees.er_fee} · Rev ₹{tallyFees.review_fee})
                          </span>
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <TInput label={`OP cases (×₹${tallyFees.regular_fee})`} value={tallyPharmOp} setValue={setTallyPharmOp} disabled={tallyData?.saved?.locked && !tallyEditMode} testid="tally-pharm-op" placeholder="0" />
                        <TInput label={`ER cases (×₹${tallyFees.er_fee})`} value={tallyPharmEr} setValue={setTallyPharmEr} disabled={tallyData?.saved?.locked && !tallyEditMode} testid="tally-pharm-er" placeholder="0" />
                        <TInput label={`Review cases (×₹${tallyFees.review_fee})`} value={tallyPharmReview} setValue={setTallyPharmReview} disabled={tallyData?.saved?.locked && !tallyEditMode} testid="tally-pharm-review" placeholder="0" />
                      </div>
                      {tallyData?.doctor_auto && (
                        <div className="grid grid-cols-3 gap-2 text-[11px] mt-1.5">
                          <DiffPill doc={tallyData.doctor_auto.op_count} pharm={tallyPharmOp} label="OP" />
                          <DiffPill doc={tallyData.doctor_auto.er_count} pharm={tallyPharmEr} label="ER" />
                          <DiffPill doc={tallyData.doctor_auto.review_count} pharm={tallyPharmReview} label="Review" />
                        </div>
                      )}
                    </div>

                    {/* Nursing services - per service detail */}
                    <div className="pt-2 border-t border-amber-200">
                      <div className="text-[11px] font-bold text-amber-800 uppercase mb-1 tracking-wider">
                        Nursing services — per item
                      </div>
                      {(() => {
                        const autoSvc = tallyData?.nursing_auto?.services || {};
                        // Build merged set of services (system + any extra in pharmacy)
                        const allKeys = Array.from(new Set([
                          ...Object.keys(autoSvc),
                          ...Object.keys(tallyPharmNursing || {}),
                        ]));
                        if (allKeys.length === 0) {
                          return <p className="text-[11px] text-amber-700 italic">No nursing services today.</p>;
                        }
                        const setSvcCount = (k, v) => {
                          setTallyPharmNursing(prev => ({ ...prev, [k]: v === '' ? 0 : parseInt(v, 10) || 0 }));
                        };
                        const totalDoc = allKeys.reduce((s, k) => s + (autoSvc[k]?.count || 0), 0);
                        const totalPh = allKeys.reduce((s, k) => s + (Number(tallyPharmNursing[k] || 0)), 0);
                        const isLocked = tallyData?.saved?.locked && !tallyEditMode;
                        return (
                          <div className="overflow-x-auto rounded border border-amber-200 bg-white">
                            <table className="min-w-full text-xs">
                              <thead className="bg-amber-100 text-amber-900">
                                <tr>
                                  <th className="px-2 py-1 text-left">Service</th>
                                  <th className="px-2 py-1 text-center w-20">Rate (₹)</th>
                                  <th className="px-2 py-1 text-center w-16">System</th>
                                  <th className="px-2 py-1 text-center w-24">Pharmacy</th>
                                  <th className="px-2 py-1 text-center w-20">Diff</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allKeys.map(k => {
                                  const sys = autoSvc[k]?.count || 0;
                                  const ph = Number(tallyPharmNursing[k] || 0);
                                  const diff = ph - sys;
                                  const breakdown = autoSvc[k]?.cost_breakdown || [];
                                  const hasBreakdown = breakdown.length > 1; // multiple rates → show sub-rows
                                  return (
                                    <React.Fragment key={k}>
                                      <tr className="border-t bg-amber-50/40">
                                        <td className="px-2 py-1 font-bold">{autoSvc[k]?.name || k}</td>
                                        <td className="px-2 py-1 text-center text-slate-500 text-[11px]">
                                          {breakdown.length === 1 ? `₹${breakdown[0].cost}` : (hasBreakdown ? 'mixed' : '—')}
                                        </td>
                                        <td className="px-2 py-1 text-center tabular-nums text-slate-600 font-semibold">{sys}</td>
                                        <td className="px-1 py-1">
                                          <input
                                            type="number"
                                            min="0"
                                            value={tallyPharmNursing[k] ?? 0}
                                            onChange={(e) => setSvcCount(k, e.target.value)}
                                            disabled={isLocked}
                                            className={`w-full h-7 rounded border border-amber-300 px-1.5 text-center text-xs font-semibold tabular-nums ${isLocked ? 'opacity-60 cursor-not-allowed bg-slate-50' : 'bg-white'}`}
                                            data-testid={`tally-pharm-svc-${k}`}
                                          />
                                        </td>
                                        <td className={`px-2 py-1 text-center tabular-nums font-bold ${
                                          diff === 0 ? 'text-slate-400' : diff > 0 ? 'text-amber-700' : 'text-rose-700'
                                        }`}>
                                          {diff === 0 ? '0' : (diff > 0 ? `+${diff}` : diff)}
                                        </td>
                                      </tr>
                                      {/* Cost-breakdown sub-rows when this service has multiple rates */}
                                      {hasBreakdown && breakdown.map((b, idx) => (
                                        <tr key={`${k}-${idx}`} className="border-t border-dashed">
                                          <td className="px-2 py-0.5 pl-6 text-slate-600 italic text-[11px]">
                                            ↳ ₹{Math.round(b.cost)} rate
                                          </td>
                                          <td className="px-2 py-0.5 text-center text-[11px] tabular-nums text-slate-500">₹{Math.round(b.cost)}</td>
                                          <td className="px-2 py-0.5 text-center text-[11px] tabular-nums text-slate-700">{b.count}</td>
                                          <td className="px-2 py-0.5 text-center text-[11px] tabular-nums text-slate-500">₹{Math.round(b.revenue)}</td>
                                          <td className="px-2 py-0.5"></td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                              <tfoot className="bg-amber-50">
                                <tr className="border-t-2 border-amber-300">
                                  <td className="px-2 py-1 font-bold text-amber-800" colSpan="2">Total items</td>
                                  <td className="px-2 py-1 text-center font-bold tabular-nums text-amber-800">{totalDoc}</td>
                                  <td className="px-2 py-1 text-center font-bold tabular-nums text-amber-800">{totalPh}</td>
                                  <td className={`px-2 py-1 text-center font-bold tabular-nums ${
                                    (totalPh - totalDoc) === 0 ? 'text-slate-500' : (totalPh - totalDoc) > 0 ? 'text-amber-700' : 'text-rose-700'
                                  }`}>
                                    {(totalPh - totalDoc) === 0 ? '0' : ((totalPh - totalDoc) > 0 ? `+${totalPh - totalDoc}` : (totalPh - totalDoc))}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* CASH MANUAL */}
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <IndianRupee className="h-4 w-4 text-emerald-700" />
                        <span className="font-semibold text-sm text-emerald-900">Cash collected</span>
                      </div>
                      <span className="text-[10px] text-emerald-700">
                        Auto from pharmacy counts × rates (override if needed)
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <TInput label="Consultation cash (₹)" value={tallyConsCash} setValue={setTallyConsCash} disabled={tallyData?.saved?.locked && !tallyEditMode} testid="tally-cash-cons" />
                      <TInput label="Nursing cash (₹)" value={tallyNursingCash} setValue={setTallyNursingCash} disabled={tallyData?.saved?.locked && !tallyEditMode} testid="tally-cash-nursing" />
                    </div>
                  </div>

                  {/* DISCREPANCY NOTE */}
                  <div className="rounded-lg border border-slate-200 p-3 space-y-1.5">
                    <label className="text-xs font-semibold text-slate-700">Discrepancy / notes</label>
                    <textarea
                      value={tallyNote}
                      onChange={(e) => setTallyNote(e.target.value)}
                      disabled={tallyData?.saved?.locked && !tallyEditMode}
                      placeholder="e.g. Mrs. X went home without paying ₹200 OP fee."
                      className="w-full min-h-[60px] border rounded-md px-2 py-1.5 text-sm"
                      data-testid="tally-note"
                    />
                  </div>

                  {/* FINAL OVERRIDE (4.c) */}
                  <div className="rounded-lg border-2 border-emerald-300 bg-white p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-emerald-700" />
                      <span className="font-bold text-sm text-emerald-900">Final reconciled totals — manual override</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <TInput label="Consultation (₹)" value={tallyFinalCons} setValue={(v) => { setTallyFinalCons(v); setTallyFinalGrand(Number(v) + Number(tallyFinalNursing)); }} disabled={tallyData?.saved?.locked && !tallyEditMode} testid="tally-final-cons" />
                      <TInput label="Nursing (₹)" value={tallyFinalNursing} setValue={(v) => { setTallyFinalNursing(v); setTallyFinalGrand(Number(tallyFinalCons) + Number(v)); }} disabled={tallyData?.saved?.locked && !tallyEditMode} testid="tally-final-nursing" />
                      <TInput label="Grand Total (₹)" value={tallyFinalGrand} setValue={setTallyFinalGrand} disabled={tallyData?.saved?.locked && !tallyEditMode} testid="tally-final-grand" highlight />
                    </div>
                  </div>

                  {/* SIGNED BY */}
                  <div className="rounded-lg border-2 border-blue-300 bg-blue-50/50 p-3 space-y-1.5">
                    <label className="text-xs font-bold text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                      <UserPlus className="h-3.5 w-3.5" /> Signed by (staff name) *
                    </label>
                    <input
                      value={tallySignedBy}
                      onChange={(e) => setTallySignedBy(e.target.value)}
                      disabled={tallyData?.saved?.locked && !tallyEditMode}
                      placeholder="e.g. Priya, Lakshmi…"
                      className={`w-full h-10 rounded-md border-2 border-blue-300 px-3 text-base font-semibold ${(tallyData?.saved?.locked && !tallyEditMode) ? 'opacity-70 cursor-not-allowed bg-slate-50' : 'bg-white'}`}
                      data-testid="tally-signed-by"
                      style={{ fontFamily: 'cursive, Brush Script MT, sans-serif' }}
                    />
                    <p className="text-[11px] text-blue-700">This name will appear on the doctor's revenue page as the verified signatory.</p>
                  </div>

                  {/* Saved meta */}
                  {tallyData?.saved && (
                    <div className="text-[11px] text-slate-500 text-right">
                      {tallyData.saved.signed_by && <>Signed by <b className="text-slate-700">{tallyData.saved.signed_by}</b> · </>}
                      Submitted by <b>{tallyData.saved.submitted_by}</b> at {new Date(tallyData.saved.submitted_at).toLocaleString('en-IN')}
                      {tallyData.saved.edited_at && <> · edited by <b>{tallyData.saved.edited_by}</b> at {new Date(tallyData.saved.edited_at).toLocaleString('en-IN')}</>}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer actions */}
            <div className="border-t p-3 bg-slate-50 flex items-center gap-2 flex-wrap">
              {!tallyData?.saved?.locked && (
                <Button onClick={submitTally} disabled={tallySubmitting || tallyLoading} className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold" data-testid="tally-submit">
                  {tallySubmitting ? 'Saving…' : 'Submit & Lock'}
                </Button>
              )}
              {tallyData?.saved?.locked && !tallyEditMode && (
                <Button onClick={() => setTallyEditMode(true)} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="tally-unlock-edit">
                  <Edit2 className="h-4 w-4 mr-1" /> Edit (admin)
                </Button>
              )}
              {tallyEditMode && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    type="password"
                    placeholder="Admin password"
                    value={tallyEditPwd}
                    onChange={(e) => setTallyEditPwd(e.target.value)}
                    className="h-9 max-w-[180px] text-sm"
                    data-testid="tally-admin-pwd"
                  />
                  <Button onClick={submitTallyEdit} disabled={tallySubmitting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tally-save-edit">
                    {tallySubmitting ? '…' : 'Save'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setTallyEditMode(false); setTallyEditPwd(''); fetchTally(tallyDate); }}>
                    Cancel
                  </Button>
                </div>
              )}
              <span className="ml-auto text-[11px] text-slate-500">Tip: doctor & nursing counts auto-refresh whenever this modal opens.</span>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// --- Helpers for Tally modal ---
const Stat = ({ label, value, highlight }) => (
  <div className={`px-2 py-1.5 rounded border ${highlight ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
    <div className="text-[10px] uppercase text-slate-500 tracking-wide">{label}</div>
    <div className={`text-sm font-bold tabular-nums ${highlight ? 'text-emerald-700' : 'text-slate-800'}`}>{value}</div>
  </div>
);

const TInput = ({ label, value, setValue, disabled, testid, highlight, placeholder }) => (
  <div>
    <label className="text-[11px] font-medium text-slate-600 block mb-0.5">{label}</label>
    <input
      type="number"
      step="0.01"
      value={value === undefined || value === null ? '' : value}
      disabled={disabled}
      placeholder={placeholder || ''}
      onChange={(e) => setValue(e.target.value === '' ? '' : parseFloat(e.target.value))}
      className={`w-full h-9 rounded-md border px-2 text-sm font-semibold tabular-nums ${highlight ? 'border-emerald-400 bg-emerald-50/40 text-emerald-800' : 'border-slate-300'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      data-testid={testid}
    />
  </div>
);

const DiffPill = ({ doc, pharm, label }) => {
  const diff = Number(pharm || 0) - Number(doc || 0);
  const color = diff === 0 ? 'bg-slate-100 text-slate-600' : diff > 0 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700';
  return (
    <div className={`flex items-center justify-between px-2 py-1 rounded ${color}`} title={`Doctor ${doc} vs Pharmacy ${pharm}`}>
      <span className="font-medium">{label}</span>
      <span className="tabular-nums font-bold">
        Doc {doc} · Ph {pharm} {diff !== 0 && <span className="ml-1">({diff > 0 ? '+' : ''}{diff})</span>}
      </span>
    </div>
  );
};

export default NursingDashboard;